'use client';

import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import type { PageSection } from '@/lib/storefront/section-library';
import {
  getByPath,
  getVariation,
  setByPath,
  type FieldDef,
  type RepeaterField,
} from '@/lib/storefront/section-library';
import type { PreviewBreakpoint, SectionStyle, SectionStylePatch } from '@/lib/storefront/section-templates';
import { resolveSectionStyle } from '@/lib/storefront/section-templates';
import { collectLinkIssues, linkStatus, type SiteLinkOption, type SitePageLink } from '@/lib/site/site-links';
import { MediaDropzone } from './MediaDropzone';

interface SectionSettingsPanelProps {
  section: PageSection | null;
  onChangeContent: (content: Record<string, any>) => void;
  onChangeStyle: (style: SectionStyle) => void;
  uploadUrl?: string;
  linkOptions?: SiteLinkOption[];
  sitePages?: SitePageLink[];
  pagesReady?: boolean;
  previewBreakpoint?: PreviewBreakpoint;
}

const inputClass =
  'mt-1 w-full rounded-lg border border-line bg-shell px-2.5 py-2 text-xs text-ink outline-none focus:border-brand';

export function SectionSettingsPanel({ section, onChangeContent, onChangeStyle, uploadUrl = '', linkOptions = [], sitePages = [], pagesReady = false, previewBreakpoint = 'desktop' }: SectionSettingsPanelProps) {
  const [tab, setTab] = useState<'content' | 'design'>('content');

  if (!section) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 bg-card p-6 text-center text-ink">
        <p className="text-sm font-semibold text-ink">Nothing selected</p>
        <p className="max-w-[15rem] text-xs text-muted">
          Click a section on the canvas to edit its text, images and styling.
        </p>
      </div>
    );
  }

  const variation = getVariation(section.category, section.variationId);
  const fields = variation?.fields ?? [];

  const setField = (key: string, value: any) => {
    onChangeContent(setByPath(section.content, key, value));
  };

  return (
    <div className="flex h-full flex-col bg-card text-ink">
      <div className="border-b border-line px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">{section.category}</p>
        <h2 className="text-sm font-semibold text-ink">{variation?.name ?? section.variationId}</h2>
      </div>

      <div className="flex border-b border-line">
        <button
          type="button"
          onClick={() => setTab('content')}
          className={`flex-1 px-3 py-2 text-xs font-semibold ${tab === 'content' ? 'border-b-2 border-brand text-ink' : 'text-muted'}`}
        >
          Content
        </button>
        <button
          type="button"
          onClick={() => setTab('design')}
          className={`flex-1 px-3 py-2 text-xs font-semibold ${tab === 'design' ? 'border-b-2 border-brand text-ink' : 'text-muted'}`}
        >
          Design
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {tab === 'content' ? (
          fields.length === 0 ? (
            <p className="text-xs text-muted">This section has no editable fields.</p>
          ) : (
            fields.map((field) => (
              <FieldControl
                key={field.key}
                field={field}
                value={getByPath(section.content, field.key)}
                onChange={(value) => setField(field.key, value)}
                uploadUrl={uploadUrl}
                linkOptions={linkOptions}
                sitePages={sitePages}
                pagesReady={pagesReady}
              />
            ))
          )
        ) : (
          <DesignControls
            style={section.style}
            onChange={onChangeStyle}
            uploadUrl={uploadUrl}
            previewBreakpoint={previewBreakpoint}
          />
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Field control
// ──────────────────────────────────────────────────────────────

function FieldControl({
  field,
  value,
  onChange,
  uploadUrl,
  linkOptions,
  sitePages,
  pagesReady,
}: {
  field: FieldDef;
  value: any;
  onChange: (value: any) => void;
  uploadUrl: string;
  linkOptions: SiteLinkOption[];
  sitePages: SitePageLink[];
  pagesReady: boolean;
}) {
  if (field.type === 'text' && isLinkField(field)) {
    return (
      <LinkField
        field={field}
        value={value}
        onChange={onChange}
        options={linkOptions}
        sitePages={sitePages}
        pagesReady={pagesReady}
      />
    );
  }

  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-xs font-medium text-ink">
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-line" />
        {field.label}
      </label>
    );
  }

  if (field.type === 'select') {
    return (
      <label className="block text-xs font-medium text-ink">
        {field.label}
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inputClass}>
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === 'number') {
    return (
      <label className="block text-xs font-medium text-ink">
        {field.label}
        <input
          type="number"
          value={value ?? 0}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(Number(e.target.value))}
          className={inputClass}
        />
      </label>
    );
  }

  if (field.type === 'color') {
    return (
      <label className="block text-xs font-medium text-ink">
        {field.label}
        <span className="mt-1 flex items-center gap-2">
          <input
            type="color"
            value={typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-12 rounded-lg border border-line"
          />
          <input value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-line bg-shell px-2.5 py-2 text-xs text-ink" />
        </span>
      </label>
    );
  }

  if (field.type === 'image') {
    return <ImageField field={field} value={value} onChange={onChange} uploadUrl={uploadUrl} />;
  }

  if (field.type === 'textarea') {
    return (
      <label className="block text-xs font-medium text-ink">
        {field.label}
        <textarea
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={field.placeholder}
          className={`${inputClass} resize-y`}
        />
        {field.hint && <span className="mt-1 block text-[11px] font-normal text-muted">{field.hint}</span>}
      </label>
    );
  }

  if (field.type === 'repeater') {
    return (
      <RepeaterFieldControl
        field={field}
        value={value}
        onChange={onChange}
        uploadUrl={uploadUrl}
        linkOptions={linkOptions}
        sitePages={sitePages}
        pagesReady={pagesReady}
      />
    );
  }

  return (
    <label className="block text-xs font-medium text-ink">
      {field.label}
      <input
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className={inputClass}
      />
      {field.hint && <span className="mt-1 block text-[11px] font-normal text-muted">{field.hint}</span>}
    </label>
  );
}

// ──────────────────────────────────────────────────────────────
// Link field with page picker
// ──────────────────────────────────────────────────────────────

/** Single-value link fields: `href`, `ctaHref`, `secondaryCtaHref`, … */
function isLinkField(field: FieldDef): boolean {
  return field.key === 'href' || /Href$/.test(field.key);
}

function PageLinkSelect({
  options,
  onPick,
  label = 'Insert a page link…',
}: {
  options: SiteLinkOption[];
  onPick: (href: string) => void;
  label?: string;
}) {
  if (options.length === 0) return null;
  const groups = Array.from(new Set(options.map((option) => option.group)));

  return (
    <select
      value=""
      onChange={(event) => {
        if (event.target.value) onPick(event.target.value);
      }}
      className={inputClass}
      aria-label={label}
    >
      <option value="">{label}</option>
      {groups.map((group) => (
        <optgroup key={group} label={group}>
          {options
            .filter((option) => option.group === group)
            .map((option) => (
              <option key={`${group}:${option.href}`} value={option.href}>
                {option.label} ({option.href})
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}

function LinkField({
  field,
  value,
  onChange,
  options,
  sitePages,
  pagesReady,
}: {
  field: FieldDef;
  value: any;
  onChange: (value: any) => void;
  options: SiteLinkOption[];
  sitePages: SitePageLink[];
  pagesReady: boolean;
}) {
  return (
    <div className="block text-xs font-medium text-ink">
      {field.label}
      <input
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={field.placeholder ?? '/vehicles'}
        className={inputClass}
      />
      <PageLinkSelect options={options} onPick={onChange} />
      <LinkWarning href={typeof value === 'string' ? value : ''} pages={sitePages} pagesReady={pagesReady} />
      {field.hint && <span className="mt-1 block text-[11px] font-normal text-muted">{field.hint}</span>}
    </div>
  );
}

function LinkWarning({
  href,
  pages,
  pagesReady,
}: {
  href: string;
  pages: SitePageLink[];
  pagesReady: boolean;
}) {
  if (!pagesReady) return null;
  const status = linkStatus(href, pages);
  if (status.kind !== 'draft' && status.kind !== 'missing') return null;

  const message = status.kind === 'draft'
    ? `“${status.label}” is still a draft — visitors will see a 404 until it is published.`
    : status.label === 'the home page is at /'
      ? 'Use / to link to the home page.'
      : `No page uses ${status.href} — visitors will see a 404.`;

  return (
    <p className="mt-1 flex items-start gap-1 text-[11px] font-semibold text-warning">
      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
      <span>{message}</span>
    </p>
  );
}

// ──────────────────────────────────────────────────────────────
// Image field with upload
// ──────────────────────────────────────────────────────────────

function ImageField({ field, value, onChange, uploadUrl }: { field: FieldDef; value: any; onChange: (v: any) => void; uploadUrl: string }) {
  return (
    <MediaDropzone
      label={field.label}
      value={typeof value === 'string' ? value : ''}
      uploadUrl={uploadUrl}
      onChange={onChange}
    />
  );
}

// ──────────────────────────────────────────────────────────────
// Repeater field
// ──────────────────────────────────────────────────────────────

function RepeaterFieldControl({
  field,
  value,
  onChange,
  uploadUrl,
  linkOptions,
  sitePages,
  pagesReady,
}: {
  field: FieldDef;
  value: any;
  onChange: (value: any) => void;
  uploadUrl: string;
  linkOptions: SiteLinkOption[];
  sitePages: SitePageLink[];
  pagesReady: boolean;
}) {
  const items: any[] = Array.isArray(value) ? value : [];

  const updateItem = (index: number, next: any) => {
    const copy = [...items];
    copy[index] = next;
    onChange(copy);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    if (field.stringItems) {
      onChange([...items, '']);
      return;
    }
    const blank: Record<string, any> = {};
    for (const sub of field.itemFields ?? []) {
      blank[sub.key] =
        sub.key === 'links' || sub.key === 'values'
          ? []
          : sub.type === 'number'
            ? 0
            : sub.type === 'boolean'
              ? false
              : '';
    }
    onChange([...items, blank]);
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const copy = [...items];
    const [moved] = copy.splice(index, 1);
    copy.splice(target, 0, moved);
    onChange(copy);
  };

  return (
    <div className="text-xs font-medium text-ink">
      {field.label}
      <div className="mt-1 space-y-2">
        {items.map((item, index) => (
          <div key={index} className="rounded-lg border border-line bg-shell/50 p-2">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">
                {field.itemLabel ?? 'Item'} {index + 1}
              </span>
              <span className="flex items-center gap-0.5">
                <button type="button" onClick={() => moveItem(index, -1)} disabled={index === 0} className="rounded p-1 text-muted hover:bg-card disabled:opacity-30" title="Move up">
                  <ChevronUp className="h-3 w-3" />
                </button>
                <button type="button" onClick={() => moveItem(index, 1)} disabled={index === items.length - 1} className="rounded p-1 text-muted hover:bg-card disabled:opacity-30" title="Move down">
                  <ChevronDown className="h-3 w-3" />
                </button>
                <button type="button" onClick={() => removeItem(index)} className="rounded p-1 text-danger hover:bg-card" title="Remove">
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            </div>

            {field.stringItems ? (
              <input
                value={typeof item === 'string' ? item : ''}
                onChange={(e) => updateItem(index, e.target.value)}
                className={inputClass}
              />
            ) : (
              <div className="space-y-2">
                {(field.itemFields ?? []).map((sub) => (
                  <SubField
                    key={sub.key}
                    sub={sub}
                    value={item?.[sub.key]}
                    onChange={(v) => updateItem(index, { ...item, [sub.key]: v })}
                    uploadUrl={uploadUrl}
                    linkOptions={linkOptions}
                    sitePages={sitePages}
                    pagesReady={pagesReady}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={addItem}
          className="inline-flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-line py-2 text-[11px] font-semibold text-muted hover:border-muted hover:bg-shell"
        >
          <Plus className="h-3 w-3" /> Add {field.itemLabel?.toLowerCase() ?? 'item'}
        </button>
      </div>
    </div>
  );
}

/** Link arrays are stored as [{label, href}] but edited as "Label|/href" lines. */
function linksToText(value: any): string {
  if (!Array.isArray(value)) return typeof value === 'string' ? value : '';
  return value
    .map((link) => (link && typeof link === 'object' ? `${link.label ?? ''}|${link.href ?? ''}` : String(link ?? '')))
    .join('\n');
}

function textToLinks(text: string) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [label, href = '#'] = line.split('|').map((part) => part.trim());
      return { label, href };
    });
}

function SubField({
  sub,
  value,
  onChange,
  uploadUrl,
  linkOptions,
  sitePages,
  pagesReady,
}: {
  sub: RepeaterField;
  value: any;
  onChange: (v: any) => void;
  uploadUrl: string;
  linkOptions: SiteLinkOption[];
  sitePages: SitePageLink[];
  pagesReady: boolean;
}) {
  if (sub.type === 'textarea') {
    const isLinkList = sub.key === 'links';
    const textValue = isLinkList ? linksToText(value) : typeof value === 'string' ? value : '';
    const linkIssues = isLinkList && pagesReady
      ? collectLinkIssues(textToLinks(textValue), sitePages)
      : [];
    return (
      <label className="block text-[11px] font-medium text-muted">
        {sub.label}
        <textarea
          value={textValue}
          onChange={(e) => onChange(isLinkList ? textToLinks(e.target.value) : e.target.value)}
          rows={isLinkList ? 4 : 2}
          className={`${inputClass} resize-y`}
        />
        {linkIssues.map((issue) => (
          <LinkWarning
            key={issue.href}
            href={issue.href}
            pages={sitePages}
            pagesReady={pagesReady}
          />
        ))}
      </label>
    );
  }

  if (sub.type === 'image') {
    return (
      <MediaDropzone
        label={sub.label}
        value={typeof value === 'string' ? value : ''}
        uploadUrl={uploadUrl}
        onChange={onChange}
      />
    );
  }

  if (sub.type === 'number') {
    return (
      <label className="block text-[11px] font-medium text-muted">
        {sub.label}
        <input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className={inputClass} />
      </label>
    );
  }

  if (sub.key === 'href' && sub.type === 'text') {
    return (
      <div className="block text-[11px] font-medium text-muted">
        {sub.label}
        <input
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="/vehicles"
          className={inputClass}
        />
        <PageLinkSelect options={linkOptions} onPick={onChange} label="Pick a page…" />
        <LinkWarning href={typeof value === 'string' ? value : ''} pages={sitePages} pagesReady={pagesReady} />
      </div>
    );
  }

  // Arrays of plain strings (e.g. comparison-table values) are edited comma separated.
  const isStringList = Array.isArray(value);
  return (
    <label className="block text-[11px] font-medium text-muted">
      {sub.label}
      <input
        value={isStringList ? value.join(', ') : value ?? ''}
        onChange={(e) =>
          onChange(isStringList ? e.target.value.split(',').map((part) => part.trim()) : e.target.value)
        }
        className={inputClass}
      />
    </label>
  );
}

// ──────────────────────────────────────────────────────────────
// Design controls
// ──────────────────────────────────────────────────────────────

function DesignControls({
  style,
  onChange,
  uploadUrl,
  previewBreakpoint,
}: {
  style: SectionStyle;
  onChange: (style: SectionStyle) => void;
  uploadUrl: string;
  previewBreakpoint: PreviewBreakpoint;
}) {
  const [editBreakpoint, setEditBreakpoint] = useState<PreviewBreakpoint>(previewBreakpoint);
  const resolved = resolveSectionStyle(style, editBreakpoint);
  const inherited = editBreakpoint !== 'desktop';

  const patch = (next: SectionStylePatch) => {
    if (editBreakpoint === 'desktop') {
      onChange({ ...style, ...next });
      return;
    }
    const key = editBreakpoint === 'tablet' ? 'tablet' : 'mobile';
    onChange({
      ...style,
      [key]: { ...(style[key] ?? {}), ...next },
    });
  };

  const clearOverride = () => {
    if (editBreakpoint === 'tablet') {
      const { tablet: _removed, ...rest } = style;
      onChange(rest);
      return;
    }
    if (editBreakpoint === 'mobile') {
      const { mobile: _removed, ...rest } = style;
      onChange(rest);
    }
  };

  const widths: SectionStyle['containerWidth'][] = ['full', 'wide', 'normal', 'narrow'];
  const hasOverride =
    (editBreakpoint === 'tablet' && style.tablet && Object.keys(style.tablet).length > 0)
    || (editBreakpoint === 'mobile' && style.mobile && Object.keys(style.mobile).length > 0);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">Breakpoint</p>
        <div className="mt-2 grid grid-cols-3 gap-1">
          {(['desktop', 'tablet', 'mobile'] as const).map((bp) => (
            <button
              key={bp}
              type="button"
              onClick={() => setEditBreakpoint(bp)}
              className={`rounded-lg border px-1 py-1.5 text-[10px] font-semibold capitalize ${
                editBreakpoint === bp ? 'border-brand bg-brand/10 text-brand' : 'border-line text-muted'
              }`}
            >
              {bp}
            </button>
          ))}
        </div>
        {inherited && (
          <p className="mt-1.5 text-[10px] text-muted">
            {hasOverride ? 'Editing overrides for this breakpoint.' : 'Inheriting desktop values until you change a control.'}
          </p>
        )}
        {hasOverride && (
          <button type="button" onClick={clearOverride} className="mt-1 text-[11px] font-semibold text-muted hover:text-ink">
            Clear {editBreakpoint} overrides
          </button>
        )}
      </div>

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">Spacing</p>
        <label className="mt-2 block text-xs font-medium text-ink">
          Padding top <span className="float-right font-mono text-muted">{resolved.paddingTop}px</span>
          <input type="range" min={0} max={160} step={4} value={resolved.paddingTop} onChange={(e) => patch({ paddingTop: Number(e.target.value) })} className="mt-1 w-full" />
        </label>
        <label className="mt-2 block text-xs font-medium text-ink">
          Padding bottom <span className="float-right font-mono text-muted">{resolved.paddingBottom}px</span>
          <input type="range" min={0} max={160} step={4} value={resolved.paddingBottom} onChange={(e) => patch({ paddingBottom: Number(e.target.value) })} className="mt-1 w-full" />
        </label>
      </div>

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">Content width</p>
        <div className="mt-2 grid grid-cols-4 gap-1">
          {widths.map((width) => (
            <button
              key={width}
              type="button"
              onClick={() => patch({ containerWidth: width })}
              className={`rounded-lg border px-1 py-1.5 text-[10px] font-semibold capitalize ${
                resolved.containerWidth === width ? 'border-brand bg-brand/10 text-brand' : 'border-line text-muted'
              }`}
            >
              {width}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">Typography</p>
        <div className="mt-2 grid grid-cols-3 gap-1">
          {(['left', 'center', 'right'] as const).map((align) => (
            <button
              key={align}
              type="button"
              onClick={() => patch({ textAlign: align })}
              className={`rounded-lg border px-1 py-1.5 text-[10px] font-semibold capitalize ${
                (resolved.textAlign ?? 'left') === align ? 'border-brand bg-brand/10 text-brand' : 'border-line text-muted'
              }`}
            >
              {align}
            </button>
          ))}
        </div>
        <label className="mt-2 flex items-center gap-2 text-xs font-medium text-ink">
          Heading
          <input type="color" value={resolved.headingColor ?? '#0f172a'} onChange={(e) => patch({ headingColor: e.target.value })} className="h-8 w-10 rounded-lg border border-line" />
        </label>
        <label className="mt-2 flex items-center gap-2 text-xs font-medium text-ink">
          Body
          <input type="color" value={resolved.bodyColor ?? '#334155'} onChange={(e) => patch({ bodyColor: e.target.value })} className="h-8 w-10 rounded-lg border border-line" />
        </label>
      </div>

      <label className="flex items-center gap-2 text-xs font-medium text-ink">
        <input
          type="checkbox"
          checked={Boolean(resolved.hidden)}
          onChange={(e) => patch({ hidden: e.target.checked })}
          className="rounded border-line"
        />
        Hide on this breakpoint
      </label>

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">Background</p>
        <div className="mt-2 grid grid-cols-4 gap-1">
          {(['none', 'color', 'gradient', 'image'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => patch({ backgroundType: type })}
              className={`rounded-lg border px-1 py-1.5 text-[10px] font-semibold capitalize ${
                resolved.backgroundType === type ? 'border-brand bg-brand/10 text-brand' : 'border-line text-muted'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {resolved.backgroundType === 'color' && (
          <label className="mt-2 flex items-center gap-2">
            <input type="color" value={resolved.backgroundColor ?? '#ffffff'} onChange={(e) => patch({ backgroundColor: e.target.value })} className="h-9 w-12 rounded-lg border border-line" />
            <input value={resolved.backgroundColor ?? ''} onChange={(e) => patch({ backgroundColor: e.target.value })} className="w-full rounded-lg border border-line bg-shell px-2 py-1.5 text-xs text-ink" placeholder="#ffffff" />
          </label>
        )}

        {resolved.backgroundType === 'gradient' && (
          <input
            value={resolved.backgroundGradient ?? ''}
            onChange={(e) => patch({ backgroundGradient: e.target.value })}
            className={inputClass}
            placeholder="linear-gradient(135deg,#0f172a,#1d4ed8)"
          />
        )}

        {resolved.backgroundType === 'image' && (
          <div className="mt-2">
            <MediaDropzone
              label="Background image"
              value={resolved.backgroundImage ?? ''}
              uploadUrl={uploadUrl}
              onChange={(url) => patch({ backgroundImage: url })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
